import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FolderKanban, Link2, Plus, RefreshCcw, Search, Send, Users, ShieldAlert } from 'lucide-react';
import { api, LoiMayChu } from '../services/api';
import type {
  BoSuuTapNhom,
  Nhom,
  TaiKhoanFB,
  GroupJoinPolicyResponse,
  UserJoinPolicyInput,
  UserJoinPolicyPayload,
} from '../services/kieu';
import { AnhDaiDien, ChuaCoDuLieu, DangTai, DauThe, NutChinh, NutPhu, The } from '../components/ui';
import { tenActor } from '../services/taiKhoan';
import { useKhoaGui } from '../services/khoaGui';
import { JoinBatchPanel } from '../components/JoinBatchPanel';
import { JoinPolicyForm } from '../components/JoinPolicyForm';
import { JoinPlanPanel } from '../components/JoinPlanPanel';
import joinMessages from '../locales/vi/joinCustomer.json';
import {
  validateJoinSubmission,
  translateJoinErrorCode,
  defaultPolicyToInput,
  serializeUserJoinPolicy,
  validateUserJoinPolicy,
} from '../services/groupJoinHelpers';

type Tab = 'nhom' | 'bo-suu-tap' | 'tham-gia';

function parseInput(raw: string): string[] {
  return [...new Set(raw.split(/[\n,\s]+/).map(x => {
    const t = x.trim();
    const m = t.match(/(?:facebook\.com\/)?groups\/([0-9a-zA-Z._-]+)/i);
    return (m ? m[1] : t).replace(/[?#].*$/, '').trim();
  }).filter(Boolean))];
}

export function ManKenhNhom({ taiKhoan }: { taiKhoan: TaiKhoanFB | null }) {
  const [tab, setTab] = useState<Tab>('nhom');
  const [nhom, setNhom] = useState<Nhom[]>([]);
  const [collections, setCollections] = useState<BoSuuTapNhom[]>([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState<string | null>(null);
  const [tuKhoa, setTuKhoa] = useState('');
  const [collectionDangChon, setCollectionDangChon] = useState('ALL');
  const [daChon, setDaChon] = useState<Set<string>>(new Set());
  const [tenBoMoi, setTenBoMoi] = useState('');
  const [boGan, setBoGan] = useState('');
  const [joinInput, setJoinInput] = useState('');
  const [joinCandidates, setJoinCandidates] = useState<Nhom[]>([]);
  const [joinSelected, setJoinSelected] = useState<Set<string>>(new Set());
  const [joinMsg, setJoinMsg] = useState<string | null>(null);
  const [dangGuiJoin, setDangGuiJoin] = useState(false);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);

  // Group join policy state (ARCH-01 Thin Client)
  const [policyResponse, setPolicyResponse] = useState<GroupJoinPolicyResponse | null>(null);
  const [policyInput, setPolicyInput] = useState<UserJoinPolicyInput>({
    delayMinMinutes: 3,
    delayMaxMinutes: 5,
    roundSize: 3,
    roundRestMinutes: 30,
    dailyLimit: 10,
  });
  const [isPolicyDirty, setIsPolicyDirty] = useState<boolean>(false);
  const isPolicyDirtyRef = useRef<boolean>(false);
  isPolicyDirtyRef.current = isPolicyDirty;

  const [policyErrors, setPolicyErrors] = useState<Record<string, string>>({});
  const [policyLoading, setPolicyLoading] = useState<boolean>(false);

  // Active actor and request cancellation ref (IDENTITY-01)
  const activeActorUidRef = useRef(taiKhoan?.uid);
  activeActorUidRef.current = taiKhoan?.uid;
  const policyAbortRef = useRef<AbortController | null>(null);

  // UI-01: Idempotency key stability, intent binding, and synchronous submit locking
  const intentKeyRef = useRef<string | null>(null);
  const lastIntentSigRef = useRef<string | null>(null);
  const frozenPayloadRef = useRef<{ groupIds: string[]; policy: UserJoinPolicyPayload; fbUid?: string } | null>(null);
  const submittingJoinRef = useRef<boolean>(false);

  // Mỗi thao tác một khoá riêng: đang đồng bộ nhóm thì vẫn phải tạo được bộ sưu
  // tập. Khoá cả màn hình là làm phiền người dùng không cần thiết (UI-01).
  const [dangTaiLai, chayTaiLai] = useKhoaGui();
  const [dangDongBo, chayDongBo] = useKhoaGui();
  const [dangTaoBo, chayTaoBo] = useKhoaGui();
  const [dangGan, chayGan] = useKhoaGui();
  const [dangTim, chayTim] = useKhoaGui();

  async function taiDuLieu() {
    try {
      setDangTai(true);
      const [g, c] = await Promise.all([api.nhom(taiKhoan?.uid), api.boSuuTapNhom(taiKhoan?.uid)]);
      setNhom(g.groups || []);
      setCollections(c.collections || []);
      setBoGan(c.collections?.[0]?.name || '');
      setLoi(g.reason ? (g.message || g.reason) : null);
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không tải được dữ liệu nhóm.');
    } finally {
      setDangTai(false);
    }
  }

  const taiChinhSach = useCallback(async (isActorChange = false) => {
    if (!taiKhoan?.uid) return;
    const requestedUid = taiKhoan.uid;

    if (policyAbortRef.current) {
      policyAbortRef.current.abort();
    }
    const abortCtrl = new AbortController();
    policyAbortRef.current = abortCtrl;

    setPolicyLoading(true);
    try {
      const res = await api.getGroupJoinPolicy(requestedUid, abortCtrl.signal);
      // Cancel / discard stale response if actor changed while request was in-flight (IDENTITY-01)
      if (activeActorUidRef.current !== requestedUid) return;

      setPolicyResponse(res);

      // Do NOT overwrite unsaved edits after refetch unless deliberate actor change
      if (isActorChange || !isPolicyDirtyRef.current) {
        if (res.defaults) {
          setPolicyInput(defaultPolicyToInput(res.defaults));
          setIsPolicyDirty(false);
          setPolicyErrors({});
        }
      }
    } catch (err: any) {
      if (activeActorUidRef.current !== requestedUid) return;
      if (err?.name === 'AbortError' || abortCtrl.signal.aborted) return;
    } finally {
      if (activeActorUidRef.current === requestedUid) {
        setPolicyLoading(false);
      }
    }
  }, [taiKhoan?.uid]);

  const handleResetDefaults = () => {
    if (policyResponse?.defaults) {
      setPolicyInput(defaultPolicyToInput(policyResponse.defaults));
      setIsPolicyDirty(false);
      setPolicyErrors({});
      // Reset intent signature so fresh key will be generated for new intent
      intentKeyRef.current = null;
      lastIntentSigRef.current = null;
      frozenPayloadRef.current = null;
    }
  };

  useEffect(() => {
    setDaChon(new Set());
    setJoinSelected(new Set());
    setJoinCandidates([]);
    intentKeyRef.current = null;
    lastIntentSigRef.current = null;
    frozenPayloadRef.current = null;
    setActiveBatchId(null);
    setIsPolicyDirty(false);
    setPolicyErrors({});
    taiDuLieu();
    taiChinhSach(true);
  }, [taiKhoan?.uid, taiChinhSach]);

  useEffect(() => {
    if (tab === 'tham-gia') {
      taiChinhSach(false);
    }
  }, [tab, taiChinhSach]);

  const nhomLoc = useMemo(() => {
    const q = tuKhoa.trim().toLowerCase();
    return nhom.filter(g => {
      if (collectionDangChon === 'UNCLASSIFIED' && g.collection_name) return false;
      if (collectionDangChon !== 'ALL' && collectionDangChon !== 'UNCLASSIFIED' && g.collection_name !== collectionDangChon) return false;
      if (!q) return true;
      return String(g.name || '').toLowerCase().includes(q) || String(g.id || '').includes(q);
    });
  }, [nhom, tuKhoa, collectionDangChon]);

  async function dongBoNhom() {
    try {
      await api.dongBoNhom();
      await taiDuLieu();
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không đồng bộ được nhóm.');
    }
  }

  async function taoBoSuuTap() {
    if (!tenBoMoi.trim()) return;
    try {
      await api.taoBoSuuTapNhom(tenBoMoi.trim());
      setTenBoMoi('');
      await taiDuLieu();
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không tạo được bộ sưu tập.');
    }
  }

  async function ganBoSuuTap() {
    if (!taiKhoan?.uid || daChon.size === 0 || !boGan) return;
    try {
      await api.ganNhomVaoBoSuuTap(taiKhoan.uid, [...daChon], boGan);
      setDaChon(new Set());
      await taiDuLieu();
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không gán được bộ sưu tập.');
    }
  }

  async function timUngVienJoin() {
    const ids = parseInput(joinInput);
    if (ids.length > 0 && ids.every(x => /^[0-9a-zA-Z._-]+$/.test(x)) && !joinInput.trim().includes(' ')) {
      if(ids.length>500) { setJoinMsg(joinMessages.listTooLarge); return; }
      const candidates = ids.map(id => ({ id, name: `Nhóm ${id}`, avatar: '', collection_name: '', url: `https://www.facebook.com/groups/${id}`, source: 'INPUT' }));
      setJoinCandidates(candidates);
      setJoinSelected(new Set(candidates.map(g => g.id)));
      intentKeyRef.current = null;
      setJoinMsg(null);
      return;
    }
    try {
      // Tìm trong nhóm đã đồng bộ (CACHE), không cam kết tìm kiếm toàn bộ Facebook
      const res = await api.timNhom(joinInput.trim(), 20);
      setJoinCandidates(res.groups || []);
      setJoinSelected(new Set());
      intentKeyRef.current = null;
      setJoinMsg(res.message || null);
    } catch (err) {
      setJoinMsg(err instanceof LoiMayChu ? err.message : 'Không tìm được nhóm trong bộ nhớ tạm.');
    }
  }

  async function guiJoin() {
    // 1. Double submit ref lock (UI-01)
    if (submittingJoinRef.current) return;

    // 2. Blocked actor notice: do not allow live join when actor is blocked (SEC-01 / DATA-02)
    if (policyResponse?.actorQuota?.isBlocked) {
      setJoinMsg(
        `Tài khoản đang bị hạn chế tham gia nhóm do Facebook yêu cầu kiểm tra (${policyResponse.actorQuota.blockedReason || 'Bị chặn'}). Không thể gửi đợt mới.`
      );
      return;
    }

    const ids = [...joinSelected];

    // 3. Validate join submission including Page policy check
    const validation = validateJoinSubmission({
      groupIds: ids,
      isPage: taiKhoan?.isPage,
      joinLimit: 500,
    });

    if (!validation.valid) {
      setJoinMsg(validation.reason || 'Yêu cầu không hợp lệ.');
      return;
    }

    // 4. Validate user policy inputs against server bounds and server daily quota
    const policyVal = validateUserJoinPolicy({
      input: { ...policyInput, roundSize: policyResponse?.defaults?.roundSize ?? 3, dailyLimit: policyResponse?.defaults?.dailyQuota ?? 10 },
      bounds: policyResponse?.bounds,
      serverDailyQuota: policyResponse?.defaults?.dailyQuota,
    });

    if (!policyVal.valid) {
      setPolicyErrors(policyVal.errors);
      setJoinMsg(policyVal.firstError || 'Thông số chính sách không hợp lệ.');
      return;
    }
    setPolicyErrors({});

    const safePolicyPayload = serializeUserJoinPolicy(policyInput);

    submittingJoinRef.current = true;
    setDangGuiJoin(true);

    // 5. Stable idempotency key: freeze key + payload on uncertain submission (UI-01)
    const actorId = taiKhoan?.uid || 'unknown';
    const sortedIds = ids.slice().sort().join('-');
    const currentIntentSig = `${actorId}:${sortedIds}:${safePolicyPayload.delayMinSec}_${safePolicyPayload.delayMaxSec}_${safePolicyPayload.roundSize}_${safePolicyPayload.roundRestSec}_${safePolicyPayload.dailyLimit}`;

    let stableKey = intentKeyRef.current;
    let payloadToSubmit: {
      groupIds: string[];
      policy: UserJoinPolicyPayload;
      fbUid?: string;
    } = {
      groupIds: ids,
      policy: safePolicyPayload,
      fbUid: taiKhoan?.uid,
    };

    if (!stableKey || lastIntentSigRef.current !== currentIntentSig) {
      // New intent: generate fresh idempotency key
      stableKey = `JOIN_GROUPS:${crypto.randomUUID()}`;
      intentKeyRef.current = stableKey;
      lastIntentSigRef.current = currentIntentSig;
      frozenPayloadRef.current = payloadToSubmit;
    } else if (frozenPayloadRef.current) {
      // Replaying existing uncertain submission: use frozen payload and key
      payloadToSubmit = frozenPayloadRef.current;
    }

    try {
      const res = await api.thamGiaNhom(payloadToSubmit.groupIds, stableKey, {
        fbUid: payloadToSubmit.fbUid,
        policy: payloadToSubmit.policy,
      });

      // POST /groups/join returns ACCEPTED batch (202), NOT joined
      const msg = joinMessages.listSubmitted;
      setJoinMsg(msg);
      if (res.batchId) {
        setActiveBatchId(res.batchId);
      }
      // Successful acceptance clears selection and resets frozen intent key
      setJoinSelected(new Set());
      intentKeyRef.current = null;
      lastIntentSigRef.current = null;
      frozenPayloadRef.current = null;

      // Refresh policy to update quota snapshot
      taiChinhSach(false);
    } catch (err: any) {
      // If error occurs, keep intentKeyRef and frozenPayloadRef for safe replay without duplicating intent
      const errMsg = err instanceof LoiMayChu
        ? translateJoinErrorCode(err.ma, err.message)
        : 'Không gửi được yêu cầu tham gia nhóm.';
      setJoinMsg(errMsg);
    } finally {
      submittingJoinRef.current = false;
      setDangGuiJoin(false);
    }
  }

  const isPageActor = Boolean(taiKhoan?.isPage);

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in space-y-5">
      <The className="p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <AnhDaiDien src={taiKhoan?.avatar} ten={tenActor(taiKhoan)} kichThuoc="h-14 w-14" />
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">Kênh & Nhóm</h2>
              <p className="truncate text-sm text-slate-500 dark:text-slate-400">Actor: {tenActor(taiKhoan)} · {taiKhoan?.uid || 'chưa chọn'} · {taiKhoan?.isPage ? 'Page' : 'Cá nhân'}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <NutPhu onClick={() => chayTaiLai(taiDuLieu)} dangChay={dangTaiLai}>
              {!dangTaiLai && <RefreshCcw className="h-3.5 w-3.5" />}
              {dangTaiLai ? 'Đang tải...' : 'Tải lại'}
            </NutPhu>
            <NutPhu onClick={() => chayDongBo(dongBoNhom)} dangChay={dangDongBo}>
              {dangDongBo ? 'Đang đồng bộ...' : 'Đồng bộ lại'}
            </NutPhu>
          </div>
        </div>
      </The>

      {loi && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">{loi}</div>}

      <div className="flex flex-wrap gap-2">
        {(['nhom', 'bo-suu-tap', 'tham-gia'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} className={'rounded-xl px-4 py-2 text-sm font-bold ' + (tab === t ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 shadow-card dark:bg-slate-800 dark:text-slate-300')}>
            {t === 'nhom' ? 'Nhóm đã đồng bộ' : t === 'bo-suu-tap' ? 'Bộ sưu tập' : 'Tìm & tham gia nhóm'}
          </button>
        ))}
      </div>

      {dangTai ? <The><DangTai dong={5} /></The> : tab === 'tham-gia' ? (
        <div className="space-y-5">
          <JoinPlanPanel actorUid={taiKhoan?.uid} onAccepted={setActiveBatchId} />
          <The className="overflow-hidden">
            <DauThe
              icon={<Send className="h-4 w-4" />}
              tieuDe="Tìm & tham gia nhóm"
              phai={<span className="text-xs font-semibold text-brand-600 dark:text-brand-400">{joinMessages.listCapacity}</span>}
            />
            <div className="space-y-4 p-5">
              {/* Notice regarding cache search and safe accepted batch */}
              <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-3.5 text-sm text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300">
                <p className="text-xs">{joinMessages.searchNote}</p>
              </div>

              {/* Disabled Page Policy Banner */}
              {isPageActor && (
                <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                  <div className="space-y-0.5">
                    <p className="font-semibold">Chế độ Fanpage bị khóa:</p>
                    <p className="text-xs">
                      Chức năng tham gia nhóm tự động hiện chỉ hỗ trợ tài khoản cá nhân, chưa hỗ trợ Fanpage (theo chính sách an toàn của hệ thống).
                    </p>
                  </div>
                </div>
              )}

              {/* JoinPolicyForm: User Join Policy Configuration & Preview */}
              <JoinPolicyForm
                policyResponse={policyResponse}
                value={policyInput}
                onChange={(val) => {
                  setPolicyInput(val);
                  setIsPolicyDirty(true);
                  // Reset frozen intent on user edit
                  intentKeyRef.current = null;
                  lastIntentSigRef.current = null;
                  frozenPayloadRef.current = null;
                }}
                onResetDefaults={handleResetDefaults}
                isDirty={isPolicyDirty}
                loading={policyLoading}
                disabled={dangGuiJoin || isPageActor}
                validationErrors={policyErrors}
              />

              <textarea
                value={joinInput}
                onChange={e => setJoinInput(e.target.value)}
                className="h-28 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                placeholder="Nhập ID, link nhóm Facebook (mỗi dòng một nhóm) hoặc từ khóa tìm trong nhóm đã đồng bộ..."
              />

              <div className="flex flex-wrap gap-2">
                <NutChinh onClick={() => chayTim(timUngVienJoin)} disabled={!joinInput.trim()} dangChay={dangTim}>
                  {!dangTim && <Search className="h-4 w-4" />}
                  {dangTim ? 'Đang tìm...' : joinMessages.prepare}
                </NutChinh>

                <NutPhu
                  onClick={guiJoin}
                  disabled={dangGuiJoin || joinSelected.size === 0 || isPageActor || Boolean(policyResponse?.actorQuota?.isBlocked)}
                  dangChay={dangGuiJoin}
                >
                  <Send className="h-4 w-4" />
                  {dangGuiJoin ? 'Đang gửi...' : `Gửi yêu cầu (${joinSelected.size})`}
                </NutPhu>
              </div>

              {joinMsg && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  {joinMsg}
                </div>
              )}

              <GroupList
                groups={joinCandidates}
                selected={joinSelected}
                onToggle={id => {
                  setJoinSelected(s => toggleSet(s, id));
                  intentKeyRef.current = null;
                  lastIntentSigRef.current = null;
                  frozenPayloadRef.current = null;
                }}
                empty="Chưa có ứng viên tham gia nhóm. Nhập ID hoặc link nhóm ở trên để bắt đầu."
              />
            </div>
          </The>

          {/* JoinBatchPanel: Dedicated Component for Durable Join Batches */}
          <JoinBatchPanel
            actorUid={taiKhoan?.uid}
            activeTab={tab}
            newBatchId={activeBatchId}
            onRefreshNeeded={taiDuLieu}
          />
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
          <The className="overflow-hidden">
            <DauThe icon={<FolderKanban className="h-4 w-4" />} tieuDe="Bộ sưu tập" dem={collections.length} />
            <div className="space-y-2 p-4">
              <CollectionButton label="Tất cả nhóm" count={nhom.length} active={collectionDangChon === 'ALL'} onClick={() => setCollectionDangChon('ALL')} />
              <CollectionButton label="Chưa phân loại" count={nhom.filter(g => !g.collection_name).length} active={collectionDangChon === 'UNCLASSIFIED'} onClick={() => setCollectionDangChon('UNCLASSIFIED')} />
              {collections.map(c => <CollectionButton key={c.id || c.name} label={c.name} count={c.group_count || 0} active={collectionDangChon === c.name} onClick={() => setCollectionDangChon(c.name)} />)}
              <div className="flex gap-2 pt-3">
                <input value={tenBoMoi} onChange={e => setTenBoMoi(e.target.value)} className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Bộ mới" />
                <button
                  onClick={() => chayTaoBo(taoBoSuuTap)}
                  disabled={dangTaoBo || !tenBoMoi.trim()}
                  aria-busy={dangTaoBo || undefined}
                  aria-label="Tạo bộ sưu tập"
                  className="rounded-lg bg-brand-600 px-3 text-white transition disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {dangTaoBo
                    ? <span aria-hidden="true" className="block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    : <Plus className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </The>
          <The className="overflow-hidden">
            <DauThe icon={<Users className="h-4 w-4" />} tieuDe={tab === 'bo-suu-tap' ? 'Gán bộ sưu tập' : 'Nhóm đã đồng bộ'} dem={nhomLoc.length} />
            <div className="space-y-4 p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input value={tuKhoa} onChange={e => setTuKhoa(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Tìm theo tên hoặc ID nhóm" />
                </div>
                <select value={boGan} onChange={e => setBoGan(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                  <option value="">Chọn bộ sưu tập</option>
                  {collections.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                </select>
                <NutChinh onClick={() => chayGan(ganBoSuuTap)} disabled={!boGan || daChon.size === 0} dangChay={dangGan}>
                  {dangGan ? 'Đang gán...' : `Gán ${daChon.size} nhóm`}
                </NutChinh>
              </div>
              <GroupList groups={nhomLoc} selected={daChon} onToggle={id => setDaChon(s => toggleSet(s, id))} empty="Chưa có nhóm nào trong bộ lọc này." />
            </div>
          </The>
        </div>
      )}
    </div>
  );
}

function toggleSet(source: Set<string>, id: string) {
  const next = new Set(source);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

function CollectionButton({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return <button onClick={onClick} className={'flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold ' + (active ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300' : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50')}><span className="truncate">{label}</span><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-700">{count}</span></button>;
}

function GroupList({ groups, selected, onToggle, empty }: { groups: Nhom[]; selected: Set<string>; onToggle: (id: string) => void; empty: string }) {
  if (groups.length === 0) return <ChuaCoDuLieu chu={empty} />;
  return <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-slate-700 dark:border-slate-700">{groups.map(g => <label key={g.id} className="flex cursor-pointer items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/40"><input type="checkbox" checked={selected.has(g.id)} onChange={() => onToggle(g.id)} className="h-4 w-4 rounded border-slate-300 text-brand-600" /><span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xs font-bold text-slate-500 dark:bg-slate-700">{g.avatar ? <img src={g.avatar} alt="" className="h-full w-full object-cover" /> : 'G'}</span><span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold text-slate-900 dark:text-white">{g.name || `Nhóm ${g.id}`}</span><span className="block truncate text-xs text-slate-500 dark:text-slate-400">ID: {g.id} · {g.collection_name || 'Chưa phân loại'} {g.source ? `· ${g.source}` : ''}</span></span>{g.url && <a href={g.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"><Link2 className="h-4 w-4" /></a>}</label>)}</div>;
}
